import deepmerge from 'deepmerge';
import { FacetValuesList, UrlSearchState, AlgoliaIndiceName, FacetValue } from '@packages/common';
import { SearchBase } from '@packages/services/algolia/helpers/';
import { checkMissingFacetValues } from './checkMissingFacetValues';

export interface ExtendedFacetValue extends FacetValue {
  facet: string;
}

export async function requestFacetValues(
  facetsMap: Record<string, string[]>,
  url: string,
  searchBase: SearchBase,
): Promise<ExtendedFacetValue[]> {
  const filters = Object.keys(facetsMap).map((facetName) => {
    const valuesFilter = facetsMap[facetName].map((facetVal) => `seoName:${facetVal}`);
    const combinedValuesFilter = valuesFilter.length === 1 ? valuesFilter[0] : `(${valuesFilter.join(' OR ')})`;

    // we could/should also add `AND status:ACTIVE` but for some reason algolia
    // returns no results then
    return `facet:${facetName} AND url:${url} AND ${combinedValuesFilter}`;
  });

  const requests = Promise.all(
    filters.map((filter) => {
      return searchBase<ExtendedFacetValue>(AlgoliaIndiceName.FACET_VALUES, {
        filters: filter,
      });
    }),
  );

  return (await requests).reduce<ExtendedFacetValue[]>((acc, cur) => {
    if (!cur || cur?.hits?.length <= 0) {
      return acc;
    }

    acc = acc.concat(cur?.hits);
    return acc;
  }, []);
}

export function facetValuesToFacetValuesList(facetValues: ExtendedFacetValue[]): FacetValuesList {
  return facetValues.reduce<FacetValuesList>((acc, cur) => {
    const facetName = cur?.facet;
    if (!facetName) {
      return acc;
    }

    if (!acc[facetName]) {
      acc[facetName] = [cur];
    } else {
      acc[facetName].push(cur);
    }

    return acc;
  }, {});
}

export function mergeFacetValuesLists(listA: FacetValuesList, listB: FacetValuesList): FacetValuesList {
  return deepmerge(listA, listB);
}

export async function replenishFacetValues(
  facetValues: FacetValuesList,
  urlSearchState: UrlSearchState,
  url: string,
  searchBase: SearchBase,
): Promise<FacetValuesList> {
  const missingFacetValues = checkMissingFacetValues(facetValues, urlSearchState);
  if (!missingFacetValues) {
    return Promise.resolve(facetValues);
  } else {
    const retrievedFacetValues = await requestFacetValues(missingFacetValues, url, searchBase);
    const newFacetValuesLists = facetValuesToFacetValuesList(retrievedFacetValues);
    return mergeFacetValuesLists(facetValues, newFacetValuesLists);
  }
}
